import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { RefreshCcw, Play, BarChart2, ChevronDown, ChevronUp, ZoomIn, ZoomOut, RotateCcw, X, ChevronLeft, ChevronRight, Settings, Trash2, Square, Download } from 'lucide-react';

const ImageModal = ({ isOpen, onClose, imageUrl, imageUrls, lotNum, items }) => {
  const colors = [
    'border-red-500 bg-red-500',
    'border-blue-500 bg-blue-500',
    'border-green-500 bg-green-500',
    'border-yellow-500 bg-yellow-500',
    'border-purple-500 bg-purple-500',
    'border-orange-500 bg-orange-500',
    'border-pink-500 bg-pink-500',
    'border-cyan-500 bg-cyan-500',
    'border-indigo-500 bg-indigo-500',
    'border-teal-500 bg-teal-500',
  ];
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (imageUrls && imageUrl) {
      // Find index by comparing path parts of URLs to be robust against SAS token differences
      const getPath = (url) => url ? url.split('?')[0] : '';
      const targetPath = getPath(imageUrl);
      const idx = imageUrls.findIndex(url => getPath(url) === targetPath);
      if (idx !== -1) setCurrentIndex(idx);
    }
  }, [imageUrl, imageUrls]);

  if (!isOpen) return null;

  const currentUrl = imageUrls && imageUrls.length > 0 ? imageUrls[currentIndex] : imageUrl;

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 5));
  const handleZoomOut = () => {
    setZoom(prev => {
      const newZoom = Math.max(prev - 0.2, 0.5);
      if (newZoom === 1) setOffset({ x: 0, y: 0 });
      return newZoom;
    });
  };
  const resetZoom = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (imageUrls && currentIndex < imageUrls.length - 1) {
      setCurrentIndex(currentIndex + 1);
      resetZoom();
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (imageUrls && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      resetZoom();
    }
  };

  let title = `Lot #${lotNum}`;
  const itemsList = Array.isArray(items) ? items : (items ? [items] : []);
  if (itemsList.length === 1 && itemsList[0].bounding_boxes && Array.isArray(itemsList[0].bounding_boxes)) {
    const bbEntry = itemsList[0].bounding_boxes[currentIndex];
    if (bbEntry && typeof bbEntry === 'object' && bbEntry.label) {
      title = `${bbEntry.label} (Lot #${lotNum})`;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4" onClick={onClose}>
      <div className="relative max-w-5xl w-full bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col h-[90vh]" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b bg-white z-10">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-slate-800">{title} {imageUrls && imageUrls.length > 1 ? `(${currentIndex + 1}/${imageUrls.length})` : ''}</h3>
            <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
              <button 
                onClick={handleZoomOut}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded transition-all text-slate-600"
                title="Zoom Out"
              >
                <ZoomOut size={18} />
              </button>
              <span className="text-xs font-mono w-12 text-center text-slate-500">{Math.round(zoom * 100)}%</span>
              <button 
                onClick={handleZoomIn}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded transition-all text-slate-600"
                title="Zoom In"
              >
                <ZoomIn size={18} />
              </button>
              <button 
                onClick={resetZoom}
                className="p-1.5 hover:bg-white hover:shadow-sm rounded transition-all text-slate-600 ml-1 border-l pl-2"
                title="Reset Zoom"
              >
                <RotateCcw size={18} />
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full transition-colors">
            <X size={24} className="text-slate-500" />
          </button>
        </div>
        
        <div 
          className={`relative flex-1 overflow-hidden bg-slate-900 flex items-center justify-center ${zoom > 1 ? 'cursor-grab' : 'cursor-default'} ${isDragging ? 'cursor-grabbing' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div 
            className="relative transition-transform duration-200 ease-out origin-center"
            style={{ 
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            }}
          >
            <img 
              src={currentUrl} 
              alt={`Lot ${lotNum}`} 
              className="max-w-full max-h-[70vh] w-auto h-auto block select-none pointer-events-none" 
            />
            {items && items.map((item, idx) => {
              const colorClass = colors[idx % colors.length];
              const [borderColor, bgColor] = colorClass.split(' ');
              
              // Use multiple bounding boxes if available, otherwise fallback to single one for first image
              let currentBox = null;
              let currentLabel = item.item || item.item_name;

              if (item.bounding_boxes && Array.isArray(item.bounding_boxes)) {
                const bbEntry = item.bounding_boxes[currentIndex];
                if (bbEntry && typeof bbEntry === 'object' && bbEntry.box) {
                  currentBox = bbEntry.box;
                  currentLabel = bbEntry.label || currentLabel;
                } else {
                  currentBox = bbEntry;
                }
              } else if (currentIndex === 0) {
                currentBox = item.bounding_box;
              }

              // Ignore [0,0,0,0] boxes which might be returned by some models despite instructions
              if (Array.isArray(currentBox) && currentBox.every(val => val === 0)) {
                currentBox = null;
              }

              return currentBox && (
                <div 
                  key={`bb-${idx}`}
                  className={`absolute border-2 ${borderColor} ${bgColor} bg-opacity-10 pointer-events-none`}
                  style={{
                    top: `${currentBox[0] / 10}%`,
                    left: `${currentBox[1] / 10}%`,
                    height: `${(currentBox[2] - currentBox[0]) / 10}%`,
                    width: `${(currentBox[3] - currentBox[1]) / 10}%`,
                  }}
                >
                  <span className={`absolute -top-6 left-0 ${bgColor} text-white text-[10px] px-1 rounded whitespace-nowrap shadow-sm`}>
                    {currentLabel}
                  </span>
                </div>
              );
            })}
          </div>

          {imageUrls && currentIndex > 0 && (
            <button 
              onClick={prevImage}
              className="absolute left-4 z-50 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all shadow-lg"
            >
              <ChevronLeft size={32} />
            </button>
          )}

          {imageUrls && currentIndex < imageUrls.length - 1 && (
            <button 
              onClick={nextImage}
              className="absolute right-4 z-50 p-2 bg-black/40 hover:bg-black/60 text-white rounded-full transition-all shadow-lg"
            >
              <ChevronRight size={32} />
            </button>
          )}
        </div>

        {imageUrls && imageUrls.length > 1 && (
          <div className="p-2 bg-slate-800 flex justify-center gap-2 overflow-x-auto border-t border-slate-700">
            {imageUrls.map((url, idx) => (
              <img 
                key={idx}
                src={url}
                onClick={() => { setCurrentIndex(idx); resetZoom(); }}
                className={`w-12 h-12 object-cover rounded cursor-pointer transition-all ${currentIndex === idx ? 'ring-2 ring-blue-500 scale-105' : 'opacity-60 hover:opacity-100'}`}
                alt={`Thumbnail ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TestRigPage = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoadError, setDataLoadError] = useState('');
  const [expandedRun, setExpandedRun] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedLotNum, setSelectedLotNum] = useState(null);
  const [selectedItems, setSelectedItems] = useState(null);
  const [modelName, setModelName] = useState('gpt-5.4');
  const [isChangingModel, setIsChangingModel] = useState(false);
  const [resizeMode, setResizeMode] = useState('original');
  const [isChangingResizeMode, setIsChangingResizeMode] = useState(false);
  const [compressionMode, setCompressionMode] = useState('none');
  const [isChangingCompressionMode, setIsChangingCompressionMode] = useState(false);
  const [useEbayRag, setUseEbayRag] = useState(false);
  const [isChangingEbayRag, setIsChangingEbayRag] = useState(false);
  const [codePromptTemplates, setCodePromptTemplates] = useState({
    ebay_rag_off: '',
    ebay_rag_on: '',
  });
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [datasetSearch, setDatasetSearch] = useState('');
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  const [datasetLoadError, setDatasetLoadError] = useState('');
  const [isDatasetDropdownOpen, setIsDatasetDropdownOpen] = useState(false);
  const [runModelSearchQuery, setRunModelSearchQuery] = useState('');
  const [runDatasetSearchQuery, setRunDatasetSearchQuery] = useState('');
  const [activeRuns, setActiveRuns] = useState([]);
  const [isStartingRun, setIsStartingRun] = useState(false);
  const [terminatingRunIds, setTerminatingRunIds] = useState({});
  const [isTerminatingAllRuns, setIsTerminatingAllRuns] = useState(false);
  const datasetDropdownRef = useRef(null);
  const isRunInProgress = activeRuns.length > 0;
  const toBool = (value, fallback = false) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
      if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
    }
    return Boolean(fallback);
  };
  const normalizeRunConfig = (context = {}, fallback = {}) => ({
    run_id: String(context?.run_id || fallback?.run_id || ''),
    pid: context?.pid ?? fallback?.pid ?? null,
    pgid: context?.pgid ?? fallback?.pgid ?? null,
    started_at: context?.started_at || fallback?.started_at || Date.now(),
    updated_at: context?.updated_at || context?.progress?.updated_at || fallback?.updated_at || null,
    model: context?.model || fallback?.model || modelName,
    resize_mode: context?.resize_mode || fallback?.resize_mode || resizeMode,
    compression_mode: context?.compression_mode || fallback?.compression_mode || compressionMode,
    dataset_prefix: context?.dataset_prefix || fallback?.dataset_prefix || '',
    dataset_name: context?.dataset_name || fallback?.dataset_name || selectedDataset?.name || selectedDataset?.prefix || 'N/A',
    data_points: context?.data_points ?? context?.total_data_points ?? context?.progress?.total_data_points ?? fallback?.data_points ?? selectedDataset?.data_points ?? '-',
    total_data_points: context?.total_data_points ?? context?.progress?.total_data_points ?? fallback?.total_data_points ?? null,
    attempted_data_points: context?.attempted_data_points ?? context?.progress?.attempted_data_points ?? fallback?.attempted_data_points ?? null,
    successful_data_points: context?.successful_data_points ?? context?.progress?.successful_data_points ?? fallback?.successful_data_points ?? null,
    skipped_data_points: context?.skipped_data_points ?? context?.progress?.skipped_data_points ?? fallback?.skipped_data_points ?? null,
    progress_percent: context?.progress_percent ?? context?.progress?.progress_percent ?? fallback?.progress_percent ?? null,
    avg_duration_seconds: context?.avg_duration_seconds ?? context?.progress?.avg_duration_seconds ?? fallback?.avg_duration_seconds ?? null,
    eta_seconds: context?.eta_seconds ?? context?.progress?.eta_seconds ?? fallback?.eta_seconds ?? null,
    current_lot: context?.current_lot ?? context?.progress?.current_lot ?? fallback?.current_lot ?? null,
    current_index: context?.current_index ?? context?.progress?.current_index ?? fallback?.current_index ?? null,
    current_attempt: context?.current_attempt ?? context?.progress?.current_attempt ?? fallback?.current_attempt ?? null,
    last_error: context?.last_error ?? context?.progress?.last_error ?? fallback?.last_error ?? '',
    phase: context?.phase || context?.progress?.phase || fallback?.phase || 'running',
    prompt_template: context?.prompt_template || fallback?.prompt_template || '',
    use_ebay_rag: toBool(context?.use_ebay_rag, toBool(fallback?.use_ebay_rag, useEbayRag)),
  });
  const normalizeActiveRuns = (contexts = []) => {
    const deduped = [];
    const seenKeys = new Set();

    contexts.forEach((ctx) => {
      if (!ctx || typeof ctx !== 'object') return;
      const normalized = normalizeRunConfig(ctx, {});
      const key = normalized.run_id || String(normalized.pid || '');
      if (key && seenKeys.has(key)) return;
      if (key) seenKeys.add(key);
      deduped.push(normalized);
    });

    deduped.sort((a, b) => {
      const ta = new Date(a.started_at).getTime();
      const tb = new Date(b.started_at).getTime();
      const safeA = Number.isFinite(ta) ? ta : 0;
      const safeB = Number.isFinite(tb) ? tb : 0;
      return safeB - safeA;
    });

    return deduped;
  };

  const fetchData = async ({ showLoading = true } = {}) => {
    if (showLoading) {
      setLoading(true);
    }
    setDataLoadError('');
    try {
      const [statusRes, resultsRes] = await Promise.all([
        axios.get('/test_status'),
        axios.get('/get_test_results')
      ]);
      const statusPayload = statusRes?.data || {};
      const promptTemplatesPayload = (statusPayload?.prompt_templates && typeof statusPayload.prompt_templates === 'object')
        ? statusPayload.prompt_templates
        : {};
      setCodePromptTemplates((previous) => ({
        ebay_rag_off: typeof promptTemplatesPayload?.ebay_rag_off === 'string'
          ? promptTemplatesPayload.ebay_rag_off
          : previous.ebay_rag_off,
        ebay_rag_on: typeof promptTemplatesPayload?.ebay_rag_on === 'string'
          ? promptTemplatesPayload.ebay_rag_on
          : previous.ebay_rag_on,
      }));
      const rawActiveRuns = Array.isArray(statusPayload?.active_runs)
        ? statusPayload.active_runs
        : (Array.isArray(statusPayload?.run_contexts)
          ? statusPayload.run_contexts
          : ((statusPayload?.run_context && typeof statusPayload.run_context === 'object') ? [statusPayload.run_context] : []));
      const normalizedActiveRuns = normalizeActiveRuns(rawActiveRuns);
      const nextIsRunning = statusPayload.is_running === true || normalizedActiveRuns.length > 0;

      setIsRunning(nextIsRunning);
      setActiveRuns(normalizedActiveRuns);

      setTestResults(Array.isArray(resultsRes.data) ? resultsRes.data : []);
    } catch (error) {
      console.error("Error fetching test rig data:", error);
      setIsRunning(false);
      setActiveRuns([]);
      setDataLoadError(error?.response?.data?.message || error?.message || 'Failed to load test rig data.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchSettings = async () => {
    try {
      const [modelRes, resizeRes, compressionRes, ebayRagRes] = await Promise.all([
        axios.get('/get_model'),
        axios.get('/get_resize_mode'),
        axios.get('/get_compression_mode'),
        axios.get('/get_use_ebay_rag')
      ]);
      if (modelRes?.data?.model) {
        setModelName(modelRes.data.model);
      }
      if (resizeRes?.data?.resize_mode) {
        setResizeMode(resizeRes.data.resize_mode);
      }
      if (compressionRes?.data?.compression_mode) {
        setCompressionMode(compressionRes.data.compression_mode);
      }
      setUseEbayRag(toBool(ebayRagRes?.data?.use_ebay_rag, false));
    } catch (error) {
      console.error('Error fetching test rig settings:', error);
    }
  };

  const handleModelChange = async (newModel) => {
    try {
      setIsChangingModel(true);
      await axios.post('/set_model', { model: newModel });
      setModelName(newModel);
    } catch (error) {
      console.error('Error setting model name:', error);
      alert('Failed to update model.');
    } finally {
      setIsChangingModel(false);
    }
  };

  const handleResizeModeChange = async (newResizeMode) => {
    try {
      setIsChangingResizeMode(true);
      const response = await axios.post('/set_resize_mode', { resize_mode: newResizeMode });
      setResizeMode(response.data.resize_mode || newResizeMode);
    } catch (error) {
      console.error('Error setting resize mode:', error);
      alert('Failed to update resize mode.');
    } finally {
      setIsChangingResizeMode(false);
    }
  };

  const handleCompressionModeChange = async (newCompressionMode) => {
    try {
      setIsChangingCompressionMode(true);
      const response = await axios.post('/set_compression_mode', { compression_mode: newCompressionMode });
      setCompressionMode(response.data.compression_mode || newCompressionMode);
    } catch (error) {
      console.error('Error setting compression mode:', error);
      alert('Failed to update compression mode.');
    } finally {
      setIsChangingCompressionMode(false);
    }
  };

  const handleUseEbayRagChange = async (nextValue) => {
    try {
      setIsChangingEbayRag(true);
      const response = await axios.post('/set_use_ebay_rag', { use_ebay_rag: nextValue });
      setUseEbayRag(toBool(response?.data?.use_ebay_rag, nextValue));
    } catch (error) {
      console.error('Error setting eBay RAG mode:', error);
      alert('Failed to update eBay RAG mode.');
    } finally {
      setIsChangingEbayRag(false);
    }
  };

  const selectDataset = (dataset) => {
    if (!dataset) return;
    setSelectedDataset(dataset);
    setDatasetSearch(dataset.name || dataset.prefix || '');
    setIsDatasetDropdownOpen(false);
  };

  const fetchDatasets = async () => {
    setIsLoadingDatasets(true);
    setDatasetLoadError('');
    try {
      const response = await axios.get('/test_datasets');
      const nextDatasets = Array.isArray(response?.data?.datasets) ? response.data.datasets : [];
      setDatasets(nextDatasets);

      if (nextDatasets.length === 0) {
        setSelectedDataset(null);
        setDatasetSearch('');
        return;
      }

      const currentPrefix = selectedDataset?.prefix || '';
      const matchingSelection = nextDatasets.find((entry) => (entry.prefix || '') === currentPrefix) || null;

      if (matchingSelection) {
        setSelectedDataset(matchingSelection);
        setDatasetSearch(matchingSelection.name || matchingSelection.prefix || '');
        return;
      }

      setSelectedDataset(null);
      setDatasetSearch('');
    } catch (error) {
      console.error('Error loading test datasets:', error);
      setDatasets([]);
      setSelectedDataset(null);
      setDatasetSearch('');
      setDatasetLoadError(error?.response?.data?.message || error?.message || 'Failed to load datasets.');
    } finally {
      setIsLoadingDatasets(false);
    }
  };

  const normalizedDatasetSearch = datasetSearch.trim().toLowerCase();
  const filteredDatasets = datasets.filter((dataset) => {
    if (!normalizedDatasetSearch) return true;
    const name = (dataset?.name || '').toLowerCase();
    const prefix = (dataset?.prefix || '').toLowerCase();
    return name.includes(normalizedDatasetSearch) || prefix.includes(normalizedDatasetSearch);
  });

  useEffect(() => {
    fetchSettings();
    fetchData({ showLoading: true });
    fetchDatasets();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datasetDropdownRef.current && !datasetDropdownRef.current.contains(event.target)) {
        setIsDatasetDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isRunInProgress) {
        fetchData({ showLoading: false });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [isRunInProgress]);

  const runTestRig = async () => {
    if (isStartingRun) return;
    if (!selectedDataset) {
      alert("Please select a dataset before running the test rig.");
      return;
    }

    setIsStartingRun(true);
    try {
      const response = await axios.post('/run_test_rig', {
        resize_mode: resizeMode,
        compression_mode: compressionMode,
        use_ebay_rag: useEbayRag,
        dataset_prefix: selectedDataset.prefix || ''
      });
      if (response.data.status === 'success') {
        if (response.data.resize_mode) {
          setResizeMode(response.data.resize_mode);
        }
        if (response.data.compression_mode) {
          setCompressionMode(response.data.compression_mode);
        }
        if (response.data.use_ebay_rag !== undefined) {
          setUseEbayRag(toBool(response.data.use_ebay_rag, useEbayRag));
        }
        if (response.data.dataset_prefix !== undefined) {
          const selectedPrefix = response.data.dataset_prefix || '';
          const matchingDataset = datasets.find((entry) => (entry.prefix || '') === selectedPrefix);
          if (matchingDataset) {
            setSelectedDataset(matchingDataset);
            setDatasetSearch(matchingDataset.name || matchingDataset.prefix || '');
          }
        }
        setIsRunning(true);
        fetchData({ showLoading: false });
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const message = error.response?.data?.message || "Unable to start test rig.";
        alert(message);
        if (message.toLowerCase().includes('was not found')) {
          setSelectedDataset(null);
          fetchDatasets();
        }
      } else {
        alert("Error starting test rig: " + (error.response?.data?.message || error.message));
      }
    } finally {
      setIsStartingRun(false);
    }
  };

  const terminateRun = async (runId) => {
    const normalizedRunId = String(runId || '').trim();
    if (!normalizedRunId) {
      alert('Cannot terminate this run because run_id is missing.');
      return;
    }
    if (!window.confirm(`Terminate experiment ${normalizedRunId.slice(0, 8)}?`)) return;

    setTerminatingRunIds((previous) => ({ ...previous, [normalizedRunId]: true }));
    try {
      const response = await axios.post('/terminate_test_rig', { run_id: normalizedRunId });
      if (response?.data?.status === 'success' || response?.data?.status === 'partial_success') {
        fetchData({ showLoading: false });
      } else {
        alert(response?.data?.message || 'Failed to terminate run.');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to terminate run.';
      alert(message);
    } finally {
      setTerminatingRunIds((previous) => {
        const next = { ...previous };
        delete next[normalizedRunId];
        return next;
      });
    }
  };

  const terminateAllRuns = async () => {
    if (!window.confirm("Terminate all currently running experiments?")) return;
    setIsTerminatingAllRuns(true);
    try {
      const response = await axios.post('/terminate_test_rig', { terminate_all: true });
      if (response?.data?.status === 'success' || response?.data?.status === 'partial_success') {
        fetchData({ showLoading: false });
      } else {
        alert(response?.data?.message || 'Failed to terminate active experiments.');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to terminate active experiments.';
      alert(message);
    } finally {
      setIsTerminatingAllRuns(false);
    }
  };

  const flushResults = async () => {
    if (!window.confirm("Are you sure you want to clear all test results? This cannot be undone.")) return;
    if (isRunInProgress) {
        alert("Cannot flush results while test rig is running.");
        return;
    }
    
    try {
      const response = await axios.post('/flush_test_results');
      if (response.data.status === 'success') {
        setTestResults([]);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error("Error flushing results:", error);
      alert("Failed to flush results.");
    }
  };

  const stats = {
    avgSpeed: testResults.length > 0 
      ? (testResults.reduce((acc, curr) => acc + (curr.avg_speed_ms || curr.avg_speed || 0), 0) / testResults.length).toFixed(0) 
      : '-',
    avgAccuracy: testResults.length > 0 
      ? (testResults.reduce((acc, curr) => acc + (curr.avg_accuracy || 0), 0) / testResults.length * 100).toFixed(1) + '%' 
      : '-',
    totalCost: testResults.length > 0 
      ? testResults.reduce((acc, curr) => acc + (curr.total_cost || 0), 0).toFixed(4) 
      : '-',
    totalRuns: testResults.length
  };
  const formatResizeDisplay = (mode) => {
    const normalized = String(mode || '').trim().toLowerCase();
    if (!normalized) return 'N/A';
    return normalized === 'original' ? 'none' : normalized;
  };
  const formatStartedAt = (value) => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'In progress' : parsed.toLocaleString();
  };
  const toFiniteCount = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    return Math.max(0, Math.trunc(parsed));
  };
  const toFiniteNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const getItemExplanationText = (item = {}) => {
    const candidates = [
      item?.explanation,
      item?.margin_analysis,
      item?.marginAnalysis,
      item?.price_explanation,
      item?.price_reasoning,
      item?.reasoning,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
    return '';
  };
  const getResultTokenCounts = (result = {}) => {
    const usage = result && typeof result.usage === 'object' ? result.usage : {};
    const input = toFiniteCount(
      result?.input_tokens_consumed ??
      result?.prompt_tokens ??
      usage?.prompt_tokens ??
      usage?.input_tokens
    );
    const output = toFiniteCount(
      result?.output_tokens_consumed ??
      result?.completion_tokens ??
      usage?.completion_tokens ??
      usage?.output_tokens
    );
    const total = toFiniteCount(
      result?.total_tokens_consumed ??
      result?.total_tokens ??
      usage?.total_tokens
    );
    const hasAnyToken = input !== null || output !== null || total !== null;
    if (!hasAnyToken) {
      return { input: null, output: null, total: null };
    }
    const resolvedInput = input ?? 0;
    const resolvedOutput = output ?? 0;
    const resolvedTotal = total ?? (resolvedInput + resolvedOutput);
    return { input: resolvedInput, output: resolvedOutput, total: resolvedTotal };
  };
  const getRunTokenTotals = (run = {}) => {
    const runInput = toFiniteCount(run?.input_tokens_consumed);
    const runOutput = toFiniteCount(run?.output_tokens_consumed);
    const runTotal = toFiniteCount(run?.total_tokens_consumed);
    if (runInput !== null || runOutput !== null || runTotal !== null) {
      const input = runInput ?? 0;
      const output = runOutput ?? 0;
      const total = runTotal ?? (input + output);
      return { input, output, total };
    }

    const runResults = Array.isArray(run?.results) ? run.results : [];
    let hasAnyResultTokens = false;
    let input = 0;
    let output = 0;
    let total = 0;
    runResults.forEach((result) => {
      const counts = getResultTokenCounts(result);
      if (counts.input === null && counts.output === null && counts.total === null) {
        return;
      }
      hasAnyResultTokens = true;
      input += counts.input;
      output += counts.output;
      total += counts.total;
    });
    if (!hasAnyResultTokens) {
      return { input: null, output: null, total: null };
    }
    if (total <= 0 && (input > 0 || output > 0)) {
      total = input + output;
    }
    return { input, output, total };
  };
  const formatTokenCount = (value) => {
    const parsed = toFiniteCount(value);
    if (parsed === null) return '-';
    return parsed.toLocaleString();
  };
  const formatEta = (valueSeconds) => {
    const totalSeconds = toFiniteNumber(valueSeconds);
    if (totalSeconds === null || totalSeconds < 0) return '-';
    const seconds = Math.round(totalSeconds);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };
  const getRunProgressMetrics = (run = {}) => {
    const total = toFiniteCount(run?.total_data_points ?? run?.data_points);
    let successful = toFiniteCount(run?.successful_data_points);
    let skipped = toFiniteCount(run?.skipped_data_points);
    let attempted = toFiniteCount(run?.attempted_data_points);
    if (successful === null) successful = 0;
    if (skipped === null) skipped = 0;
    if (attempted === null) attempted = successful + skipped;
    if (total !== null) attempted = Math.min(total, attempted);

    const explicitPercent = toFiniteNumber(run?.progress_percent);
    const progressPercent = explicitPercent !== null
      ? Math.max(0, Math.min(100, explicitPercent))
      : (
        total && total > 0
          ? Math.max(0, Math.min(100, (attempted / total) * 100))
          : 0
      );
    const avgDurationSeconds = toFiniteNumber(run?.avg_duration_seconds);
    const etaSeconds = toFiniteNumber(run?.eta_seconds);
    return {
      total,
      attempted,
      successful,
      skipped,
      progressPercent,
      avgDurationSeconds,
      etaSeconds,
      currentLot: run?.current_lot,
      currentIndex: toFiniteCount(run?.current_index),
      currentAttempt: toFiniteCount(run?.current_attempt),
      phase: run?.phase || 'running',
      lastError: run?.last_error || '',
      updatedAt: run?.updated_at || null,
    };
  };
  const getRunPointStats = (run = {}) => {
    const attempted = toFiniteCount(
      run?.attempted_data_points ?? run?.data_points ?? run?.iterations ?? run?.num_iterations
    );
    const successful = toFiniteCount(
      run?.successful_data_points ?? (Array.isArray(run?.results) ? run.results.length : null)
    );
    const explicitSkipped = toFiniteCount(run?.skipped_data_points);
    const inferredSkipped = (
      attempted !== null && successful !== null
        ? Math.max(0, attempted - successful)
        : null
    );
    const skipped = explicitSkipped !== null ? explicitSkipped : (inferredSkipped !== null ? inferredSkipped : 0);
    return { attempted, successful, skipped };
  };
  const formatRunDataPoints = (run = {}) => {
    const { attempted, successful, skipped } = getRunPointStats(run);
    if (attempted !== null && successful !== null) {
      return skipped > 0 ? `${successful}/${attempted}` : `${attempted}`;
    }
    if (attempted !== null) return `${attempted}`;
    if (successful !== null) return `${successful}`;
    return '-';
  };
  const getRunDataPointsHint = (run = {}) => {
    const { attempted, successful, skipped } = getRunPointStats(run);
    if (attempted !== null && successful !== null) {
      return skipped > 0
        ? `Successful ${successful} of ${attempted} (${skipped} skipped)`
        : `Successful ${successful} of ${attempted}`;
    }
    if (attempted !== null) return `Data points: ${attempted}`;
    if (successful !== null) return `Data points: ${successful}`;
    return '';
  };
  const EBAY_RAG_PROMPT_MARKER = 'Additional context (retrieved from eBay image search):';
  const EBAY_RAG_PROMPT_SUFFIX_PREVIEW = (
    'Additional context (retrieved from eBay image search):\n' +
    '[{"title":"example title","price":"0","currency":"USD","condition":"example condition","itemWebUrl":"https://example.com","itemId":"example-id"}]\n' +
    'Use this only as supporting market context. Prioritize visual evidence in the uploaded images and do not copy titles blindly if they do not match.'
  );
  const stripEbayRagPromptBlock = (promptTemplate) => {
    const text = String(promptTemplate || '');
    const markerIndex = text.indexOf(EBAY_RAG_PROMPT_MARKER);
    if (markerIndex === -1) return text.trim();
    return text.slice(0, markerIndex).trim();
  };
  const withEbayRagPromptPreview = (promptTemplate, ragEnabled) => {
    const base = stripEbayRagPromptBlock(promptTemplate);
    const enabled = toBool(ragEnabled, false);
    if (!enabled) return base;
    if (!base) return EBAY_RAG_PROMPT_SUFFIX_PREVIEW;
    return `${base}\n\n${EBAY_RAG_PROMPT_SUFFIX_PREVIEW}`;
  };
  const formatExportTimestamp = (value) => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
  };
  const escapeCsvCell = (value) => {
    let text = '';
    if (value !== null && value !== undefined) {
      if (typeof value === 'object') {
        try {
          text = JSON.stringify(value);
        } catch (_) {
          text = String(value);
        }
      } else {
        text = String(value);
      }
    }
    if (/[",\r\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };
  const computeAccuracyRatio = (actualValue, estimateValue) => {
    const actual = toFiniteNumber(actualValue);
    const estimate = toFiniteNumber(estimateValue);
    if (actual === null && estimate === null) return null;
    const safeActual = actual ?? 0;
    const safeEstimate = estimate ?? 0;
    if (safeActual === 0 && safeEstimate === 0) return 1;
    if (safeActual === 0 || safeEstimate === 0) return 0;
    return Math.min(safeActual, safeEstimate) / Math.max(safeActual, safeEstimate);
  };
  const uniqueNonEmptyUrls = (values = []) => {
    const seen = new Set();
    const unique = [];
    values.forEach((url) => {
      if (typeof url !== 'string') return;
      const trimmed = url.trim();
      if (!trimmed || seen.has(trimmed)) return;
      seen.add(trimmed);
      unique.push(trimmed);
    });
    return unique;
  };
  const buildRunExportRows = (run = {}) => {
    const runResults = Array.isArray(run?.results) ? run.results : [];
    const runTimestamp = formatExportTimestamp(run?.timestamp);
    const rows = [];

    runResults.forEach((result, resultIndex) => {
      const resultItems = Array.isArray(result?.items) && result.items.length > 0 ? result.items : [null];
      const resultImageUrls = Array.isArray(result?.image_urls)
        ? result.image_urls
        : (typeof result?.image_urls === 'string' && result.image_urls.trim() ? [result.image_urls] : []);
      const lotNumber = result?.['lot#'] ?? '';
      const durationSeconds = toFiniteNumber(result?.duration);
      const durationMs = durationSeconds === null ? '' : Number((durationSeconds * 1000).toFixed(0));
      const cost = toFiniteNumber(result?.cost);
      const resultTokenTotals = getResultTokenCounts(result);
      const estimatedSum = Array.isArray(result?.items)
        ? result.items.reduce((sum, entry) => sum + (toFiniteNumber(entry?.estimated) || 0), 0)
        : 0;
      const lotActual = Array.isArray(result?.items)
        ? result.items.map((entry) => toFiniteNumber(entry?.actual)).find((value) => value !== null)
        : null;
      const lotAccuracy = computeAccuracyRatio(lotActual, estimatedSum);

      resultItems.forEach((item, itemIndex) => {
        const itemImageUrls = Array.isArray(item?.image_urls)
          ? item.image_urls
          : (typeof item?.image_urls === 'string' && item.image_urls.trim() ? [item.image_urls] : []);
        const singleItemImageUrl = typeof item?.image_url === 'string' && item.image_url.trim()
          ? [item.image_url]
          : [];
        const mergedImageUrls = uniqueNonEmptyUrls([...resultImageUrls, ...itemImageUrls, ...singleItemImageUrl]);

        const actual = toFiniteNumber(item?.actual);
        const estimated = toFiniteNumber(item?.estimated);
        const itemAccuracy = computeAccuracyRatio(actual, estimated);
        const explanation = getItemExplanationText(item);

        rows.push({
          run_timestamp: runTimestamp,
          run_id: run?.run_id || '',
          dataset_name: run?.dataset_name || '',
          dataset_prefix: run?.dataset_prefix || '',
          model: run?.model || '',
          mode: run?.mode || '',
          resize_mode: run?.resize_mode || '',
          compression_mode: run?.compression_mode || '',
          use_ebay_rag: toBool(run?.use_ebay_rag, false) ? 'true' : 'false',
          lot_number: item?.['lot#'] ?? lotNumber,
          result_index: resultIndex,
          item_index: item ? itemIndex : '',
          item_name: item?.item || item?.item_name || '',
          actual_price: actual === null ? '' : actual,
          estimated_price: estimated === null ? '' : estimated,
          ai_estimated_sum: estimatedSum,
          item_accuracy_percent: itemAccuracy === null ? '' : Number((itemAccuracy * 100).toFixed(2)),
          lot_accuracy_percent: lotAccuracy === null ? '' : Number((lotAccuracy * 100).toFixed(2)),
          explanation: explanation || '',
          duration_ms: durationMs,
          cost_usd: cost === null ? '' : Number(cost.toFixed(6)),
          input_tokens_consumed: resultTokenTotals.input,
          output_tokens_consumed: resultTokenTotals.output,
          total_tokens_consumed: resultTokenTotals.total,
          image_urls: mergedImageUrls.join(' | '),
          bounding_box: item?.bounding_box || '',
          raw_item: item || '',
        });
      });
    });

    return rows;
  };
  const toSafeFilenamePart = (value, fallback = '') => {
    const normalized = String(value || '')
      .trim()
      .replace(/[^a-z0-9_-]+/gi, '-')
      .replace(/^-+|-+$/g, '');
    return normalized || fallback;
  };
  const exportRunToCsv = (run = {}, displayedIndex = 0) => {
    const rows = buildRunExportRows(run);
    if (rows.length === 0) {
      alert('No detailed row results found for this test run.');
      return;
    }

    const headers = [
      'run_timestamp',
      'run_id',
      'dataset_name',
      'dataset_prefix',
      'model',
      'mode',
      'resize_mode',
      'compression_mode',
      'use_ebay_rag',
      'lot_number',
      'result_index',
      'item_index',
      'item_name',
      'actual_price',
      'estimated_price',
      'ai_estimated_sum',
      'item_accuracy_percent',
      'lot_accuracy_percent',
      'explanation',
      'duration_ms',
      'cost_usd',
      'input_tokens_consumed',
      'output_tokens_consumed',
      'total_tokens_consumed',
      'image_urls',
      'bounding_box',
      'raw_item',
    ];

    const csvLines = [
      headers.join(','),
      ...rows.map((row) => headers.map((header) => escapeCsvCell(row[header])).join(',')),
    ];
    const blob = new Blob([csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(blob);

    const runPart = toSafeFilenamePart(run?.run_id, `run-${displayedIndex + 1}`);
    const timestampPart = toSafeFilenamePart(formatExportTimestamp(run?.timestamp).replace(/[:.]/g, '-'), 'timestamp');
    const fileName = `test-run-${runPart}-${timestampPart}.csv`;

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  };
  const displayedTestResults = [...testResults].reverse();
  const normalizedRunModelSearchQuery = runModelSearchQuery.trim().toLowerCase();
  const normalizedRunDatasetSearchQuery = runDatasetSearchQuery.trim().toLowerCase();
  const runMatchesSearch = (run = {}) => {
    const model = String(run?.model || '').toLowerCase();
    const datasetName = String(run?.dataset_name || '').toLowerCase();
    const datasetPrefix = String(run?.dataset_prefix || '').toLowerCase();
    const modelMatches = !normalizedRunModelSearchQuery || model.includes(normalizedRunModelSearchQuery);
    const datasetMatches = (
      !normalizedRunDatasetSearchQuery ||
      datasetName.includes(normalizedRunDatasetSearchQuery) ||
      datasetPrefix.includes(normalizedRunDatasetSearchQuery)
    );
    return modelMatches && datasetMatches;
  };
  const filteredActiveRuns = activeRuns.filter(runMatchesSearch);
  const filteredDisplayedTestResults = displayedTestResults.filter(runMatchesSearch);
  const runSearchSummary = [
    normalizedRunModelSearchQuery ? `model "${runModelSearchQuery.trim()}"` : '',
    normalizedRunDatasetSearchQuery ? `dataset "${runDatasetSearchQuery.trim()}"` : '',
  ].filter(Boolean).join(' and ');
  const primaryActiveRun = activeRuns.length > 0 ? activeRuns[0] : null;
  const primaryRunProgress = primaryActiveRun ? getRunProgressMetrics(primaryActiveRun) : null;
  const codePromptTemplate = useEbayRag
    ? codePromptTemplates.ebay_rag_on
    : codePromptTemplates.ebay_rag_off;
  const visiblePromptTemplate = withEbayRagPromptPreview(
    codePromptTemplate || '',
    useEbayRag
  );
  const datasetBadgeText = selectedDataset
    ? `${selectedDataset.name}${selectedDataset.data_points !== undefined ? ` (${selectedDataset.data_points} data points)` : ''}`
    : (
      isLoadingDatasets
        ? 'Loading datasets...'
        : (datasetLoadError
          ? 'Dataset load failed'
          : (datasets.length > 0 ? 'Select a dataset to run' : 'No datasets available'))
    );

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 bg-white rounded-lg shadow-md">
      <div className="flex flex-col gap-3 mb-6 sm:mb-8">
        {/* Row 1: Title + Dataset badge */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Test Rig</h1>
          <div className="flex items-center px-3 sm:px-4 py-2 bg-blue-50 text-blue-700 rounded border border-blue-100">
            <BarChart2 size={18} className="mr-2 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium">
              Dataset: {datasetBadgeText}
            </span>
          </div>
        </div>
        {/* Row 2: Dropdowns */}
        <div className="flex flex-col md:flex-row md:items-stretch md:flex-nowrap gap-2">
          <div ref={datasetDropdownRef} className="relative md:flex-[2.4]">
            <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 h-full">
              <span className="text-sm font-medium text-slate-700 mr-2">Dataset:</span>
              <input
                value={datasetSearch}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  const normalized = nextValue.trim().toLowerCase();
                  const exactMatch = datasets.find((dataset) => {
                    const datasetName = (dataset?.name || '').trim().toLowerCase();
                    const datasetPrefix = (dataset?.prefix || '').trim().toLowerCase();
                    return normalized && (datasetName === normalized || datasetPrefix === normalized);
                  }) || null;
                  setDatasetSearch(nextValue);
                  setSelectedDataset(exactMatch);
                  setIsDatasetDropdownOpen(true);
                }}
                onFocus={() => setIsDatasetDropdownOpen(true)}
                disabled={isLoadingDatasets}
                placeholder={isLoadingDatasets ? 'Loading datasets...' : 'Search datasets...'}
                className={`w-full bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium ${
                  isLoadingDatasets ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
            </div>
            {isDatasetDropdownOpen && (
              <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl">
                {isLoadingDatasets ? (
                  <div className="px-3 py-2 text-xs text-slate-500">Loading datasets...</div>
                ) : filteredDatasets.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500">
                    {datasetLoadError
                      ? `Failed to load datasets: ${datasetLoadError}`
                      : (datasets.length > 0 ? 'No matching datasets.' : 'No datasets available.')}
                  </div>
                ) : (
                  filteredDatasets.map((dataset) => (
                    <button
                      key={dataset.id || dataset.prefix || '__root__'}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectDataset(dataset);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${
                        (selectedDataset?.prefix || '') === (dataset.prefix || '') ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                      }`}
                    >
                      <div className="font-semibold">{dataset.name}</div>
                      <div className="text-[11px] text-slate-500">
                        {(dataset.prefix || '__root__')} • {dataset.data_points || 0} data points • {dataset.image_count || 0} images
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 md:flex-[1.6]">
            <Settings size={18} className="text-slate-500 mr-2" />
            <span className="text-sm font-medium text-slate-700 mr-2">Model:</span>
            <select 
              value={modelName}
              onChange={(e) => handleModelChange(e.target.value)}
              disabled={isChangingModel}
              className={`bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium w-full ${
                isChangingModel ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <optgroup label="GPT-5 Series (Latest)">
                <option value="gpt-5.4">gpt-5.4</option>
                <option value="gpt-5.4-mini">gpt-5.4-mini</option>
                <option value="gpt-5.2">gpt-5.2</option>
                <option value="gpt-5.1">gpt-5.1</option>
                <option value="gpt-5">gpt-5</option>
              </optgroup>
              <optgroup label="Gemini">
                <option value="gemini-flash-latest">gemini-flash-latest</option>
                <option value="gemini-2.5-pro">gemini-2.5-pro</option>
                <option value="gemini-2.5-flash">gemini-2.5-flash</option>
                <option value="gemini-2.0-flash">gemini-2.0-flash</option>
              </optgroup>
              <optgroup label="Claude">
                <option value="claude-sonnet-4-5">claude-sonnet-4-5</option>
                <option value="claude-haiku-4-5">claude-haiku-4-5</option>
                <option value="claude-opus-4-6">claude-opus-4-6</option>
              </optgroup>
              <optgroup label="GPT-4 Series">
                <option value="gpt-4o">gpt-4o</option>
                <option value="gpt-4o-mini">gpt-4o-mini</option>
                <option value="gpt-4.1">gpt-4.1</option>
                <option value="gpt-4.1-mini">gpt-4.1-mini</option>
              </optgroup>
              <optgroup label="Reasoning Models">
                <option value="o1">o1</option>
                <option value="o3-mini">o3-mini</option>
                <option value="o3">o3</option>
                <option value="o4-mini">o4-mini</option>
              </optgroup>
            </select>
            {isChangingModel && <span className="text-xs text-slate-400 ml-2">Updating...</span>}
          </div>
          <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 md:flex-[1.2]">
            <span className="text-sm font-medium text-slate-700 mr-2">Resize:</span>
            <select
              value={resizeMode}
              onChange={(e) => handleResizeModeChange(e.target.value)}
              disabled={isChangingResizeMode}
              className={`bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium w-full ${
                isChangingResizeMode ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <option value="original">None</option>
              <option value="1600">Max 1600px</option>
              <option value="1280">Max 1280px</option>
              <option value="1024">Max 1024px</option>
              <option value="768">Max 768px</option>
              <option value="512">Max 512px</option>
              <option value="128">Max 128px</option>
            </select>
            {isChangingResizeMode && <span className="text-xs text-slate-400 ml-2">Updating...</span>}
          </div>
          <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 md:flex-[1.2]">
            <span className="text-sm font-medium text-slate-700 mr-2">Compression:</span>
            <select
              value={compressionMode}
              onChange={(e) => handleCompressionModeChange(e.target.value)}
              disabled={isChangingCompressionMode}
              className={`bg-white border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-500 text-xs font-medium w-full ${
                isChangingCompressionMode ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <option value="none">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            {isChangingCompressionMode && <span className="text-xs text-slate-400 ml-2">Updating...</span>}
          </div>
          <div className="flex items-center bg-slate-50 p-2 rounded-lg border border-slate-200 md:flex-[1.2]">
            <span className="text-sm font-medium text-slate-700 mr-2 whitespace-nowrap">eBay RAG:</span>
            <button
              type="button"
              onClick={() => handleUseEbayRagChange(!useEbayRag)}
              disabled={isChangingEbayRag}
              className={`w-full rounded px-2 py-1 text-xs font-medium border transition-colors ${
                useEbayRag
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-300 bg-white text-slate-600'
              } ${isChangingEbayRag ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}
            >
              {isChangingEbayRag ? 'Updating...' : (useEbayRag ? 'Enabled' : 'Disabled')}
            </button>
          </div>
        </div>
        {/* Row 3: Action buttons */}
        <div className="flex items-center gap-2 sm:gap-3 md:justify-end">
            <button 
              onClick={runTestRig}
              disabled={isStartingRun || isRunning === null || !selectedDataset}
              className={`flex items-center flex-1 sm:flex-none justify-center px-4 py-2 rounded text-white font-medium transition-colors ${
                isStartingRun || isRunning === null || !selectedDataset ? 'bg-slate-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              <Play size={18} className="mr-1.5" />
              <span className="text-sm">{isStartingRun ? 'Starting...' : isRunning === null ? 'Checking...' : 'Run Test'}</span>
            </button>
            <button 
              onClick={() => {
                fetchData();
                fetchDatasets();
              }}
              className="flex items-center px-3 py-2 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
              title="Refresh data"
            >
              <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
              <span className="text-sm ml-1.5 hidden sm:inline">Refresh</span>
            </button>
            <button 
              onClick={flushResults}
              disabled={isRunInProgress || isRunning === null || testResults.length === 0}
              className={`flex items-center px-3 py-2 rounded text-white transition-colors ${
                isRunInProgress || isRunning === null || testResults.length === 0 ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
              }`}
              title="Clear all test results"
            >
              <Trash2 size={18} />
              <span className="text-sm ml-1.5 hidden sm:inline">Flush</span>
            </button>
        </div>
        <div className="bg-slate-900 rounded-lg border border-slate-700 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-300 mb-2">
            Prompt Template
          </div>
          <pre className="m-0 text-[11px] leading-relaxed whitespace-pre-wrap break-words text-slate-100 font-mono max-h-48 overflow-y-auto">
            {loading
              ? 'Loading prompt template...'
              : (visiblePromptTemplate || 'No prompt template available from backend code.')}
          </pre>
        </div>
        {loading && activeRuns.length === 0 && (
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 text-xs text-slate-500">
            Checking for running experiments...
          </div>
        )}
        {activeRuns.length > 0 && (
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                Running Experiments ({activeRuns.length})
              </div>
              <button
                type="button"
                onClick={terminateAllRuns}
                disabled={isTerminatingAllRuns}
                className={`inline-flex items-center px-2.5 py-1.5 rounded text-xs font-semibold text-white transition-colors ${
                  isTerminatingAllRuns ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                <Square size={13} className="mr-1.5" />
                {isTerminatingAllRuns ? 'Terminating...' : 'Terminate All'}
              </button>
            </div>
            <div className="space-y-2">
              {activeRuns.map((run, idx) => {
                const runKey = run.run_id || String(run.pid || idx);
                const isTerminatingRun = !!terminatingRunIds[run.run_id];
                const progress = getRunProgressMetrics(run);
                const processedLabel = progress.total !== null
                  ? `${progress.attempted}/${progress.total}`
                  : `${progress.attempted}`;
                return (
                  <div key={runKey} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white border border-blue-100 rounded p-2.5">
                    <div className="text-xs text-slate-700 flex-1 min-w-0">
                      <div className="font-semibold">
                        {(run.dataset_name || run.dataset_prefix || 'Container root')} • {run.model || 'N/A'}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {formatStartedAt(run.started_at)} • resize {formatResizeDisplay(run.resize_mode)} • compression {run.compression_mode || 'none'} • eBay RAG {run.use_ebay_rag ? 'on' : 'off'}
                      </div>
                      <div className="mt-1 text-[11px] text-blue-800">
                        Progress {processedLabel} ({progress.progressPercent.toFixed(1)}%) • success {progress.successful} • skipped {progress.skipped} • ETA {formatEta(progress.etaSeconds)}
                        {progress.currentLot ? ` • lot #${progress.currentLot}` : ''}
                        {progress.currentAttempt ? ` • attempt ${progress.currentAttempt}` : ''}
                      </div>
                      <div className="mt-1.5 h-1.5 bg-blue-100 rounded overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all duration-500"
                          style={{ width: `${Math.max(0, Math.min(100, progress.progressPercent))}%` }}
                        />
                      </div>
                      {progress.lastError && (
                        <div className="mt-1 text-[11px] text-amber-700 truncate" title={progress.lastError}>
                          Last error: {progress.lastError}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => terminateRun(run.run_id)}
                      disabled={!run.run_id || isTerminatingRun}
                      className={`inline-flex items-center justify-center px-2.5 py-1.5 rounded text-xs font-semibold text-white transition-colors ${
                        !run.run_id || isTerminatingRun ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      <Square size={13} className="mr-1.5" />
                      {isTerminatingRun ? 'Terminating...' : 'Terminate'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>


      <div className="mb-10 p-10 bg-slate-50 rounded-lg border border-slate-200 text-center">
        <div className="flex flex-col items-center justify-center max-w-lg mx-auto">
          <BarChart2 size={64} className="text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-700 mb-2">Detailed Analytics Engine</h2>
              <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                <div 
                  className="bg-green-500 h-full transition-all duration-1000" 
                  style={{
                    width: loading
                      ? '35%'
                      : (isRunInProgress
                        ? `${Math.max(2, Math.min(100, primaryRunProgress?.progressPercent ?? 0))}%`
                        : '100%')
                  }}
                ></div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {loading
                  ? 'Loading test rig status...'
                  : (isRunInProgress
                    ? `Processing ${primaryRunProgress?.attempted ?? 0}/${primaryRunProgress?.total ?? '?'} lots (${(primaryRunProgress?.progressPercent ?? 0).toFixed(1)}%), ETA ${formatEta(primaryRunProgress?.etaSeconds)}`
                    : 'System ready for full dataset pass')}
              </p>
            </div>
          </div>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-xl font-semibold text-slate-800">Recent Test Runs</h3>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <input
            value={runModelSearchQuery}
            onChange={(event) => {
              setRunModelSearchQuery(event.target.value);
              setExpandedRun(null);
            }}
            placeholder="Filter by model..."
            className="w-full sm:w-56 bg-white border border-slate-300 rounded px-3 py-1.5 outline-none focus:border-blue-500 text-sm text-slate-700"
          />
          <input
            value={runDatasetSearchQuery}
            onChange={(event) => {
              setRunDatasetSearchQuery(event.target.value);
              setExpandedRun(null);
            }}
            placeholder="Filter by dataset..."
            className="w-full sm:w-56 bg-white border border-slate-300 rounded px-3 py-1.5 outline-none focus:border-blue-500 text-sm text-slate-700"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="p-4 font-semibold text-slate-700"></th>
              <th className="p-4 font-semibold text-slate-700">Timestamp</th>
              <th className="p-4 font-semibold text-slate-700">Model</th>
              <th className="p-4 font-semibold text-slate-700">Resize</th>
              <th className="p-4 font-semibold text-slate-700">Compression</th>
              <th className="p-4 font-semibold text-slate-700">eBay RAG</th>
              <th className="p-4 font-semibold text-slate-700">Dataset</th>
              <th className="p-4 font-semibold text-slate-700">Mode</th>
              <th className="p-4 font-semibold text-slate-700">Data Points</th>
              <th className="p-4 font-semibold text-slate-700">Avg Speed</th>
              <th className="p-4 font-semibold text-slate-700">Accuracy</th>
              <th className="p-4 font-semibold text-slate-700">Cost</th>
              <th className="p-4 font-semibold text-slate-700">Input Tokens</th>
              <th className="p-4 font-semibold text-slate-700">Output Tokens</th>
            </tr>
          </thead>
          <tbody>
            {filteredActiveRuns.map((runningRun, ridx) => (
              (() => {
                const progress = getRunProgressMetrics(runningRun);
                const processed = progress.total !== null
                  ? `${progress.attempted}/${progress.total}`
                  : `${progress.attempted}`;
                const progressHint = `Success ${progress.successful}, skipped ${progress.skipped}, ETA ${formatEta(progress.etaSeconds)}`;
                return (
                  <tr
                    key={`running-${runningRun.run_id || runningRun.pid || ridx}`}
                    className="border-b border-blue-100 bg-blue-50/70"
                  >
                    <td className="p-4 text-sm text-blue-700">
                      <RefreshCcw size={16} className="animate-spin" />
                    </td>
                    <td className="p-4 text-sm text-blue-700 font-medium">{formatStartedAt(runningRun.started_at)}</td>
                    <td className="p-4 text-sm text-blue-700">{runningRun.model || 'N/A'}</td>
                    <td className="p-4 text-sm text-blue-700">{formatResizeDisplay(runningRun.resize_mode)}</td>
                    <td className="p-4 text-sm text-blue-700">{runningRun.compression_mode || 'N/A'}</td>
                    <td className="p-4 text-sm text-blue-700">{runningRun.use_ebay_rag ? 'On' : 'Off'}</td>
                    <td className="p-4 text-sm text-blue-700">{runningRun.dataset_name || runningRun.dataset_prefix || 'Container root'}</td>
                    <td className="p-4 text-sm">
                      <span className="px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                        RUNNING {progress.progressPercent.toFixed(0)}%
                      </span>
                    </td>
                    <td className="p-4 text-sm text-blue-700" title={progressHint}>
                      {processed}
                    </td>
                    <td className="p-4 text-sm text-blue-700">
                      {progress.avgDurationSeconds !== null ? `${(progress.avgDurationSeconds * 1000).toFixed(0)} ms` : '-'}
                    </td>
                    <td className="p-4 text-sm text-blue-700">-</td>
                    <td className="p-4 text-sm text-blue-700">-</td>
                    <td className="p-4 text-sm text-blue-700">-</td>
                    <td className="p-4 text-sm text-blue-700">-</td>
                  </tr>
                );
              })()
            ))}
            {filteredDisplayedTestResults.map((run, idx) => {
              const { attempted, successful, skipped } = getRunPointStats(run);
              const runTokenTotals = getRunTokenTotals(run);
              const runDataPointsHint = getRunDataPointsHint(run);
              const canExportRun = Array.isArray(run?.results) && run.results.length > 0;
              return (
                <React.Fragment key={idx}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setExpandedRun(expandedRun === idx ? null : idx)}>
                    <td className="p-4 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        {expandedRun === idx ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            exportRunToCsv(run, idx);
                          }}
                          disabled={!canExportRun}
                          className={`inline-flex items-center rounded border px-1.5 py-1 text-[11px] font-semibold transition-colors ${
                            canExportRun
                              ? 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
                              : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                          }`}
                          title={canExportRun ? 'Export this test run to CSV' : 'No row-level results to export'}
                          aria-label="Export run to CSV"
                        >
                          <Download size={12} className="mr-1" />
                          CSV
                        </button>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-slate-600">{new Date(run.timestamp).toLocaleString()}</td>
                    <td className="p-4 text-sm text-slate-600">{run.model || 'N/A'}</td>
                    <td className="p-4 text-sm text-slate-600">{formatResizeDisplay(run.resize_mode)}</td>
                    <td className="p-4 text-sm text-slate-600">{run.compression_mode || 'N/A'}</td>
                    <td className="p-4 text-sm text-slate-600">{toBool(run.use_ebay_rag, false) ? 'On' : 'Off'}</td>
                    <td className="p-4 text-sm text-slate-600">{run.dataset_name || run.dataset_prefix || 'Container root'}</td>
                    <td className="p-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            run.mode === 'MOCK' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                        }`}>
                            {run.mode || 'UNKNOWN'}
                        </span>
                    </td>
                    <td className="p-4 text-sm text-slate-600" title={runDataPointsHint || undefined}>{formatRunDataPoints(run)}</td>
                    <td className="p-4 text-sm text-slate-600">{(run.avg_speed_ms || run.avg_speed || 0).toFixed(0)} ms</td>
                    <td className="p-4 text-sm font-bold text-slate-700">{(run.avg_accuracy * 100 || 0).toFixed(1)}%</td>
                    <td className="p-4 text-sm text-slate-600">${(run.total_cost || 0).toFixed(4)}</td>
                    <td className="p-4 text-sm text-slate-600">{formatTokenCount(runTokenTotals.input)}</td>
                    <td className="p-4 text-sm text-slate-600">{formatTokenCount(runTokenTotals.output)}</td>
                  </tr>
                  {expandedRun === idx && (
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <td colSpan="14" className="p-4">
                        <div className="space-y-3">
                          <div className={`rounded border p-2 text-xs ${
                            skipped > 0 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          }`}>
                            Data points: {successful ?? attempted ?? 0}
                            {attempted !== null ? ` successful out of ${attempted}` : ''}
                            {skipped > 0 ? ` (${skipped} skipped)` : ''}
                          </div>
                          <div className="rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">
                            Tokens consumed: input {formatTokenCount(runTokenTotals.input)} • output {formatTokenCount(runTokenTotals.output)} • total {formatTokenCount(runTokenTotals.total)}
                          </div>
                          {Array.isArray(run.skipped_lots) && run.skipped_lots.length > 0 && (
                            <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                              Skipped lots: {run.skipped_lots.slice(0, 12).join(', ')}
                              {run.skipped_lots.length > 12 ? `, +${run.skipped_lots.length - 12} more` : ''}
                            </div>
                          )}
                          <div className="bg-slate-900 rounded border border-slate-700 p-3">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-300 mb-2">
                              Prompt Template
                            </div>
                            <pre className="m-0 text-[11px] leading-relaxed whitespace-pre-wrap break-words text-slate-100 font-mono max-h-64 overflow-y-auto">
                              {withEbayRagPromptPreview(run.prompt_template || '', run.use_ebay_rag) || 'Prompt template was not stored for this run.'}
                            </pre>
                          </div>
                          <div className="bg-white rounded border border-slate-200 shadow-inner overflow-hidden">
                            <table className="w-full text-left text-xs">
                              <thead>
                                <tr className="bg-slate-100">
                                  <th className="p-2 border-r border-b">Lot#</th>
                                  <th className="p-2 border-r border-b">Image</th>
                                  <th className="p-2 border-r border-b">Item Name</th>
                                  <th className="p-2 border-r border-b">Actual Price</th>
                                  <th className="p-2 border-r border-b">AI Estimate</th>
                                  <th className="p-2 border-r border-b">Explanation</th>
                                  <th className="p-2 border-r border-b">AI Est. Sum</th>
                                  <th className="p-2 border-r border-b">Accuracy</th>
                                  <th className="p-2 border-r border-b">Duration</th>
                                  <th className="p-2 border-r border-b">Cost</th>
                                  <th className="p-2 border-r border-b">Input Tokens</th>
                                  <th className="p-2 border-b">Output Tokens</th>
                                </tr>
                              </thead>
                              <tbody>
                                {run.results?.map((res, ridx) => {
                                  const itemCount = res.items?.length || 1;
                                  const resultTokenTotals = getResultTokenCounts(res);
                                  return (
                                  <React.Fragment key={ridx}>
                                    {res.items?.map((item, iidx) => (
                                      <tr key={`${ridx}-${iidx}`} className={`${iidx < itemCount - 1 ? '' : 'border-b'} hover:bg-slate-50`}>
                                        {iidx === 0 && (
                                          <td className="p-2 border-r align-top" rowSpan={itemCount}>{item['lot#'] || res['lot#'] || '-'}</td>
                                        )}
                                        {iidx === 0 && (
                                          <td className="p-2 border-r align-top" rowSpan={itemCount}>
                                            <div className="flex flex-wrap gap-1 max-w-[150px]">
                                              {(res.image_urls || item.image_urls || [item.image_url]).filter(url => url).map((url, uidx) => (
                                                <img 
                                                  key={uidx}
                                                  src={url} 
                                                  alt={`${item.item} ${uidx}`} 
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedImage(url);
                                                      setSelectedImages(res.image_urls || item.image_urls || [item.image_url]);
                                                      setSelectedLotNum(item['lot#'] || res['lot#']);
                                                      setSelectedItems(res.items);
                                                  }}
                                                  className="w-10 h-10 object-cover rounded shadow-sm hover:scale-[1.5] hover:z-10 transition-transform cursor-pointer"
                                                />
                                              ))}
                                              {!(res.image_urls?.length || item.image_urls?.length || item.image_url) && (
                                                <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center text-[10px] text-slate-400">No Img</div>
                                              )}
                                            </div>
                                          </td>
                                        )}
                                        <td className="p-2 border-r font-medium">{item.item}</td>
                                        {iidx === 0 && (
                                          <td className="p-2 border-r align-top" rowSpan={itemCount}>${item.actual?.toFixed(2)}</td>
                                        )}
                                        <td className="p-2 border-r">${item.estimated?.toFixed(2)}</td>
                                        <td className="p-2 border-r text-slate-600 max-w-xs">
                                          <div className="line-clamp-4" title={getItemExplanationText(item) || 'No explanation provided.'}>
                                            {getItemExplanationText(item) || 'No explanation provided.'}
                                          </div>
                                        </td>
                                        {iidx === 0 && (
                                          <td className="p-2 border-r align-top font-bold" rowSpan={itemCount}>${res.items.reduce((sum, i) => sum + (i.estimated || 0), 0).toFixed(2)}</td>
                                        )}
                                        {iidx === 0 && (() => {
                                          const aiSum = res.items.reduce((sum, i) => sum + (i.estimated || 0), 0);
                                          const actual = item.actual || 0;
                                          const acc = actual !== 0 && aiSum !== 0 ? Math.min(aiSum, actual) / Math.max(aiSum, actual) : (actual === 0 && aiSum === 0 ? 1 : 0);
                                          return (
                                            <td className="p-2 border-r align-top" rowSpan={itemCount}>
                                              <span className={`px-1.5 py-0.5 rounded font-bold ${
                                                acc > 0.8 ? 'bg-green-100 text-green-700' : 
                                                acc > 0.5 ? 'bg-yellow-100 text-yellow-700' : 
                                                'bg-red-100 text-red-700'
                                              }`}>
                                                {(acc * 100).toFixed(1)}%
                                              </span>
                                            </td>
                                          );
                                        })()}
                                        {iidx === 0 && (
                                          <td className="p-2 border-r align-top" rowSpan={itemCount}>{(res.duration * 1000).toFixed(0)} ms</td>
                                        )}
                                        {iidx === 0 && (
                                          <td className="p-2 border-r align-top" rowSpan={itemCount}>${res.cost?.toFixed(4)}</td>
                                        )}
                                        {iidx === 0 && (
                                          <td className="p-2 border-r align-top" rowSpan={itemCount}>{formatTokenCount(resultTokenTotals.input)}</td>
                                        )}
                                        {iidx === 0 && (
                                          <td className="p-2 align-top" rowSpan={itemCount}>{formatTokenCount(resultTokenTotals.output)}</td>
                                        )}
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {loading && (
              <tr>
                <td colSpan="14" className="p-8 text-center text-slate-400 italic">Loading test runs...</td>
              </tr>
            )}
            {!loading && dataLoadError && (
              <tr>
                <td colSpan="14" className="p-8 text-center text-red-500 italic">
                  Failed to load test runs: {dataLoadError}
                </td>
              </tr>
            )}
            {!loading && !dataLoadError && displayedTestResults.length === 0 && activeRuns.length === 0 && (
              <tr>
                <td colSpan="14" className="p-8 text-center text-slate-400 italic">No test runs found yet.</td>
              </tr>
            )}
            {!loading && !dataLoadError && (displayedTestResults.length > 0 || activeRuns.length > 0) && filteredDisplayedTestResults.length === 0 && filteredActiveRuns.length === 0 && (
              <tr>
                <td colSpan="14" className="p-8 text-center text-slate-400 italic">
                  No test runs match {runSearchSummary || 'the current filters'}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <ImageModal 
        isOpen={!!selectedImage} 
        onClose={() => {
            setSelectedImage(null);
            setSelectedImages([]);
            setSelectedLotNum(null);
            setSelectedItems(null);
        }}
        imageUrl={selectedImage}
        imageUrls={selectedImages}
        lotNum={selectedLotNum}
        items={selectedItems}
      />
    </div>
  );
};

export default TestRigPage;
